// ----------------------------------------------------------------------
// (c) Copyright 2020, Dark Star Systems, Inc.  All rights reserved.
// This file may contain proprietary and confidential information.
// DO NOT COPY or distribute in any form without prior written consent.
// ----------------------------------------------------------------------

import {BaseError} from 'make-error-cause'
import {KeyframeList} from '@/keyframe'
import yaml from 'js-yaml'

class CameraError extends BaseError {}

export interface CameraKeyframes {
  baseR?: KeyframeList;
  baseTheta?: KeyframeList;
  baseX?: KeyframeList;
  baseY?: KeyframeList;
  standHeight: KeyframeList;
  boomLength: KeyframeList;
  boomRotation: KeyframeList;
  boomElevation: KeyframeList;
  roll: KeyframeList;
  lookAt: KeyframeList;
}

export interface CameraPose {
  baseX: number;
  baseY: number;
  standHeight: number;
  boomLength: number;
  boomRotation: number;
  boomElevation: number;
  roll: number;
  lookAt: Array<number>;
}

/** Read a camera rig with keyframes from a dsscene-file string. */
export function readCameraFromScene(sceneText: string): CameraKeyframes {
  const scene = (yaml.safeLoad(sceneText) as {scene: any}).scene
  const camera = scene ? scene[0] : undefined
  if (!camera || camera['type'] !== 'camera')
    throw new Error('Camera not found in scene file.')

  /** Read a set of keyframes for `name`.
      If no such name exists, return a new empty keyframe list,
      or `valIfMissing` if that is defined. */
  const getKeys = (name: string, valIfMissing?: any): KeyframeList => {
    try {
      const keys = KeyframeList.fromSceneObject(camera[name])
      if (!keys)
        return valIfMissing === undefined ? new KeyframeList(0) : valIfMissing
      return keys
    } catch (err) {
      throw new CameraError(`getting keys for ${name}`, err)
    }
  }

  const kfs = {
    baseR: getKeys('baseR', null),
    baseTheta: getKeys('baseTheta', null),
    baseX: getKeys('baseX', null),
    baseY: getKeys('baseY', null),
    standHeight: getKeys('standHeight', new KeyframeList(1)),
    boomLength: getKeys('boomLength'),
    boomRotation: getKeys('boomRotation'),
    boomElevation: getKeys('boomElevation'),
    roll: getKeys('roll'),
    lookAt: getKeys('lookAt'),
  }
  if (!kfs.baseR && !kfs.baseTheta) {
    if (!kfs.baseX && !kfs.baseY) {
      // nothing specified; use 0,0
      kfs.baseX = new KeyframeList(0)
      kfs.baseY = new KeyframeList(0)
    } else if (!kfs.baseX || !kfs.baseY) {
      throw new CameraError(`Camera keyframes must have both baseX and baseY if one is given`)
    }
    // else baseX && baseY specified; OK
  } else if (!kfs.baseR || !kfs.baseTheta) {
    throw new CameraError(`Camera keyframes must have both baseR and baseTheta if one is given`)
  }
  // else baseR && baseTheta specified; OK
  return kfs
}

/** Get the camera base position as cartesian (x,y) */
function getBasePosition(keyframes: CameraKeyframes, time: number) {
  if (keyframes.baseX && keyframes.baseY) {
    return [keyframes.baseX.getValueAtTime(time) as number,
            keyframes.baseY.getValueAtTime(time) as number]
  } else if (keyframes.baseR && keyframes.baseTheta) {
    const r = keyframes.baseR.getValueAtTime(time) as number
    const theta = keyframes.baseTheta.getValueAtTime(time) as number
    return [r * Math.cos(theta), r * Math.sin(theta)]
  } else {
    return [0, 0]
  }
}

/** Get camera rig pose at a given time.
    Always returns cartesian base position for simplicity. */
export function getCameraPose(keyframes: CameraKeyframes, time: number): CameraPose {
  const position = getBasePosition(keyframes, time)
  const pose: CameraPose = {
    baseX: position[0],
    baseY: position[1],
    standHeight: keyframes.standHeight.getValueAtTime(time) as number,
    boomLength: keyframes.boomLength.getValueAtTime(time) as number,
    boomRotation: keyframes.boomRotation.getValueAtTime(time) as number,
    boomElevation: keyframes.boomElevation.getValueAtTime(time) as number,
    roll: keyframes.roll.getValueAtTime(time) as number,
    lookAt: keyframes.lookAt.getValueAtTime(time) as number[]
  }
  return pose
}
